import { isGenericFormField } from "./genericFieldSupport";

const owns = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const isId = (value) => typeof value === "string" && value.trim() !== "";

const isScalar = (value) =>
  typeof value === "string" ||
  typeof value === "boolean" ||
  (typeof value === "number" && Number.isFinite(value));

export const hasGenericPrefillValue = (value) =>
  isScalar(value) && value !== "";

// Formik values are name-keyed, but prefill relationships remain instance-keyed.
// Convert only the current document's raw widgets so duplicate generic field IDs
// never collapse into one source value.
export const buildGenericPrefillSourceValuesByInstanceId = (
  fields = [],
  values = {},
) =>
  new Map(
    fields.flatMap((field) =>
      isId(field?.field_instance_id) &&
      isId(field?.name) &&
      owns(values, field.name)
        ? [[field.field_instance_id, values[field.name]]]
        : [],
    ),
  );

// Callers must supply one document's raw widgets, never a field_id-deduped list.
// Names and labels are never used to identify a prefill relationship.
export const getGenericPrefillTargetKey = (formId, fieldInstanceId) =>
  isId(formId) && isId(fieldInstanceId)
    ? JSON.stringify([formId, fieldInstanceId])
    : null;

const belongsToDocument = (field, formId) =>
  field && (field.form_id == null || field.form_id === formId);

const getConfigurationError = (prefill) => {
  if (!prefill || typeof prefill !== "object" || Array.isArray(prefill)) {
    return "Invalid prefill configuration.";
  }

  if (prefill.mode === "manual") {
    return owns(prefill, "value") && isScalar(prefill.value)
      ? ""
      : "Enter a text, number, or boolean prefill value.";
  }

  if (prefill.mode !== "field" || !isId(prefill.source_field_id)) {
    return "Select a source form field.";
  }

  if (
    prefill.source_field_instance_id != null &&
    !isId(prefill.source_field_instance_id)
  ) {
    return "The source field instance ID is invalid.";
  }

  return "";
};

const getSourceFields = (fields, target, prefill) => {
  const sourceInstanceId = prefill.source_field_instance_id;

  if (sourceInstanceId != null) {
    const matches = fields.filter(
      (field) => field?.field_instance_id === sourceInstanceId,
    );

    if (
      matches.length !== 1 ||
      matches[0].field_id !== prefill.source_field_id
    ) {
      return {
        fields: [],
        error:
          "The source instance must match the source field on this document.",
      };
    }

    if (sourceInstanceId === target.field_instance_id) {
      return { fields: [], error: "A field cannot prefill itself." };
    }

    return { fields: matches, error: "" };
  }

  if (prefill.source_field_id === target.field_id) {
    return { fields: [], error: "A field cannot prefill itself." };
  }

  const matches = fields.filter(
    (field) => field?.field_id === prefill.source_field_id,
  );

  // A generic tag describes a type, not a shared value. Never collapse instances.
  if (matches.length > 1 && matches.some(isGenericFormField)) {
    return {
      fields: [],
      error:
        "Select a specific same-document instance for this generic source.",
    };
  }

  return { fields: matches, error: "" };
};

// Returns a displayable error, or an empty string. Missing source *values* are
// allowed: the source need not have a widget on the document at all.
export const getGenericPrefillError = ({
  formId,
  fields = [],
  targetInstanceId,
  prefill,
}) => {
  if (!getGenericPrefillTargetKey(formId, targetInstanceId)) {
    return "Save the document and widget before configuring prefill.";
  }

  const documentFields = fields.filter((field) =>
    belongsToDocument(field, formId),
  );
  const targets = documentFields.filter(
    (field) => field.field_instance_id === targetInstanceId,
  );

  if (targets.length !== 1 || !isGenericFormField(targets[0])) {
    return "Prefill requires a unique generic-tagged target instance.";
  }

  // Null explicitly removes the configuration; undefined is not a save value.
  if (prefill === null) return "";
  const configurationError = getConfigurationError(prefill);
  if (configurationError) return configurationError;

  const visiting = new Set();
  const visited = new Set();

  const visit = (field) => {
    const configuration = field === targets[0] ? prefill : field.prefill;
    if (!isGenericFormField(field) || configuration == null) return "";

    if (!isId(field.field_instance_id)) {
      return "A prefill dependency is missing its field instance ID.";
    }
    if (
      documentFields.filter(
        (candidate) => candidate.field_instance_id === field.field_instance_id,
      ).length !== 1
    ) {
      return "A prefill dependency has an ambiguous field instance ID.";
    }

    const error = getConfigurationError(configuration);
    if (error) return error;
    if (configuration.mode === "manual") return "";
    if (visiting.has(field))
      return "Prefill would create a circular dependency.";
    if (visited.has(field)) return "";

    const sources = getSourceFields(documentFields, field, configuration);
    if (sources.error) return sources.error;

    visiting.add(field);
    for (const source of sources.fields) {
      const dependencyError = visit(source);
      if (dependencyError) return dependencyError;
    }
    visiting.delete(field);
    visited.add(field);
    return "";
  };

  return visit(targets[0]);
};

// Apply defaults to raw widgets before adapting them to UI control bindings.
// valuesByInstanceId is a Map for this document only; an entry (even a blank)
// represents an explicit saved/user value and must not be replaced by prefill.
// sourceValuesByFieldId is the backend's document-scoped prefill_source_values.
// sourceValuesByInstanceId contains the final first-run UI values for source
// widgets after ordinary property/owner/default prefills have been applied.
// No existing regular-field prefill or editability property is modified here.
export const applyGenericFieldPrefills = ({
  formId,
  fields = [],
  sourceValuesByFieldId = {},
  sourceValuesByInstanceId = new Map(),
  valuesByInstanceId = new Map(),
  isDocumentCreated = false,
}) => {
  const errorsByInstanceId = new Map();
  const documentFields = fields.filter((field) =>
    belongsToDocument(field, formId),
  );
  const resolved = new Map();
  const resolving = new Set();

  const resolve = (field) => {
    if (resolved.has(field)) return resolved.get(field);
    // Defensive guard in addition to configuration-time graph validation.
    if (resolving.has(field)) return { available: false };

    const instanceId = field.field_instance_id;
    if (isGenericFormField(field) && field.prefill != null) {
      const error = getGenericPrefillError({
        formId,
        fields: documentFields,
        targetInstanceId: instanceId,
        prefill: field.prefill,
      });

      if (error) {
        errorsByInstanceId.set(instanceId, error);
        const unavailable = { available: false };
        resolved.set(field, unavailable);
        return unavailable;
      }
    }

    const hasExplicitValue =
      isId(instanceId) && valuesByInstanceId.has(instanceId);
    const currentValue = hasExplicitValue
      ? valuesByInstanceId.get(instanceId)
      : field.value;
    const current = {
      available: isScalar(currentValue),
      value: currentValue,
    };

    if (
      !isGenericFormField(field) ||
      field.prefill == null ||
      hasExplicitValue ||
      isDocumentCreated ||
      hasGenericPrefillValue(currentValue)
    ) {
      resolved.set(field, current);
      return current;
    }

    resolving.add(field);
    const prefill = field.prefill;
    let result = { available: false };

    if (prefill.mode === "manual") {
      result = { available: true, value: prefill.value };
    } else if (
      prefill.source_field_instance_id == null &&
      owns(sourceValuesByFieldId, prefill.source_field_id)
    ) {
      // An authoritative blank/null must not fall back to a stale widget value.
      const value = sourceValuesByFieldId[prefill.source_field_id];
      result = { available: isScalar(value), value };
    } else {
      const sources = getSourceFields(documentFields, field, prefill);
      const candidates = sources.fields.map((source) => {
        const sourceInstanceId = source?.field_instance_id;
        if (
          !(isGenericFormField(source) && source.prefill != null) &&
          isId(sourceInstanceId) &&
          sourceValuesByInstanceId.has(sourceInstanceId)
        ) {
          const value = sourceValuesByInstanceId.get(sourceInstanceId);
          return { available: isScalar(value), value };
        }

        return resolve(source);
      });
      if (
        candidates.length &&
        candidates.every((candidate) => candidate.available)
      ) {
        const value = candidates[0].value;
        if (
          candidates.every((candidate) => Object.is(candidate.value, value))
        ) {
          result = { available: true, value };
        } else {
          errorsByInstanceId.set(
            instanceId,
            "Source widgets have conflicting values; select a specific source instance.",
          );
        }
      }
    }

    resolving.delete(field);
    resolved.set(field, result);
    return result;
  };

  let changed = false;
  const nextFields = fields.map((field) => {
    if (
      !isId(formId) ||
      !belongsToDocument(field, formId) ||
      !isGenericFormField(field) ||
      field.prefill == null
    ) {
      return field;
    }

    const result = resolve(field);
    if (!result.available || Object.is(field.value, result.value)) return field;

    changed = true;
    return { ...field, value: result.value };
  });

  return { fields: changed ? nextFields : fields, errorsByInstanceId };
};
